import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { INTERRUPTED_ERROR, isSteerMessage, steersAsMessages } from '@crowdex/agent-contracts';
import {
  type ArtifactListingEntry,
  type ConversationCard,
  personaOfMessage,
} from '@crowdex/agent-contracts/api';
import { groupTurnMetadata, isGroupPassReply } from '@crowdex/groups';
import { Message, MessageAction, MessageActions, MessageContent } from '@crowdex/ui/ai/message';
import { AudioLines, Pencil } from 'lucide-react';
import {
  Fragment,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import {
  DesktopMessagePart,
  QuestionToolCard,
  ToolCallGroup,
} from '@/components/desktop-message-part';
import { BackgroundTerminalCard } from '@/components/desktop-message-part/background-terminal-card';
import { CommandCard } from '@/components/desktop-message-part/command-card';
import { DynamicTool } from '@/components/desktop-message-part/dynamic-tool';
import { GroupTurnMarker } from '@/components/desktop-message-part/group-turn-marker';
import { MessageChangesCard } from '@/components/desktop-message-part/message-changes-card';
import {
  MESSAGE_ACTION,
  MESSAGE_ACTIONS_ROOM,
  MessageCopyAction,
} from '@/components/desktop-message-part/message-copy-action';
import { MessageSources } from '@/components/desktop-message-part/message-sources';
import { PersonaReply } from '@/components/desktop-message-part/persona-reply';
import { MessagePreviews } from '@/components/desktop-message-part/ref-preview';
import { SubagentsCard } from '@/components/desktop-message-part/subagent-view';
import { ToolImageStrip } from '@/components/desktop-message-part/tool-image-strip';
import { TurnSteps } from '@/components/desktop-message-part/turn-steps';
import {
  conversationWorkspaceImages,
  WorkspaceImagesProvider,
} from '@/components/desktop-message-part/workspace-image';
import { OriginCard, useChildConversation } from '@/components/desktop-shell/child-conversation';
import { LastReplyActions } from '@/components/desktop-shell/last-reply-actions';
import { ConversationAppCard } from '@/features/apps/conversation-cards';
import { messageChangedFiles, shouldShowMessageChanges } from '@/lib/conversations/message-changes';
import {
  groupMessageParts,
  type MessagePartRenderItem,
} from '@/lib/conversations/message-part/message-grouping';
import { messageText } from '@/lib/conversations/message-part/message-text';
import { toolDetail } from '@/lib/conversations/message-part/tool-identity';
import {
  type TurnLayout,
  turnLayout,
  turnSegments,
  turnWaitsOnUser,
} from '@/lib/conversations/message-part/turn-steps';
import { previewWorkspaceImages } from '@/lib/desktop-preview-transcript';
import type { MessageChangeControls } from '@/lib/desktop-shell/message-change-controls';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import {
  SELECTION_TOOLBAR_BUTTON_CLASS,
  SELECTION_TOOLBAR_CLASS,
  SELECTION_TOOLBAR_DIVIDER_BUTTON_CLASS,
} from '@/lib/side-panel/constants';
import {
  rangeElement,
  selectionToolbarStyle,
  selectionWithin,
  useTouchSelection,
} from '@/lib/side-panel/text-selection';

const MessageParts = memo(function MessageParts({
  message,
  isStreaming,
  onOpenSubagent,
  onOpenPlan,
  onOpenTerminal,
  onReviewFile,
}: {
  message: CrowdexUIMessage;
  isStreaming: boolean;
  onOpenSubagent?: (toolCallId: string) => void;
  onOpenPlan?: () => void;
  onOpenTerminal?: (terminalId: string) => void;
  onReviewFile?: (message: CrowdexUIMessage, path: string) => void;
}) {
  const subagentToolCallIds = new Set(
    message.parts
      .filter((part) => part.type === 'data-subagent')
      .map((part) => part.data.toolCallId),
  );
  return (
    <>
      {renderMessageParts(
        message,
        isStreaming,
        subagentToolCallIds,
        onOpenSubagent,
        onOpenPlan,
        onOpenTerminal,
        onReviewFile,
      )}
    </>
  );
});

function renderMessageParts(
  message: CrowdexUIMessage,
  isStreaming: boolean,
  subagentToolCallIds: ReadonlySet<string>,
  onOpenSubagent?: (toolCallId: string) => void,
  onOpenPlan?: () => void,
  onOpenTerminal?: (terminalId: string) => void,
  onReviewFile?: (message: CrowdexUIMessage, path: string) => void,
) {
  const { items } = groupMessageParts(message.id, message.parts, subagentToolCallIds);
  const renderItem = (item: MessagePartRenderItem) => {
    switch (item.kind) {
      case 'question':
        return <QuestionToolCard key={item.key} part={item.part} />;
      case 'sources':
        return <MessageSources key={item.key} parts={item.parts} />;
      case 'standalone':
        return (
          <DesktopMessagePart
            key={item.key}
            isStreaming={isStreaming && item.isLast}
            onOpenPlan={onOpenPlan}
            onOpenSubagent={onOpenSubagent}
            onOpenTerminal={onOpenTerminal}
            part={item.part}
          />
        );
      case 'single-activity':
        return (
          <DesktopMessagePart
            key={item.key}
            onOpenSubagent={onOpenSubagent}
            onOpenTerminal={onOpenTerminal}
            part={item.part}
          />
        );
      case 'background-terminal': {
        // A server or a watcher the agent started: its card, Open shows its terminal (D11).
        const { terminalId } = item.terminal.data;
        return (
          <BackgroundTerminalCard
            command={toolDetail(item.tool, null)}
            data={item.terminal.data}
            key={item.key}
            {...(onOpenTerminal === undefined ? {} : { onOpen: () => onOpenTerminal(terminalId) })}
          />
        );
      }
      case 'tool-terminal':
        return (
          <DynamicTool
            key={item.key}
            onOpenTerminal={onOpenTerminal}
            part={item.tool}
            terminal={item.terminal}
          />
        );
      case 'group':
        return <ToolCallGroup key={item.key} onOpenTerminal={onOpenTerminal} parts={item.parts} />;
      case 'tool-images':
        return <ToolImageStrip key={item.key} parts={item.parts} />;
      default:
        return null;
    }
  };

  const rendered: ReactNode[] =
    message.role === 'assistant'
      ? [
          <TurnView
            isStreaming={isStreaming}
            items={items}
            key={`${message.id}-turn`}
            message={message}
            onOpenSubagent={onOpenSubagent}
            onOpenTerminal={onOpenTerminal}
            onReviewFile={onReviewFile}
            renderItem={renderItem}
          />,
        ]
      : items.map((item) => renderItem(item));

  return rendered;
}

/**
 * An agent's turn (design D10–D12), in transcript order: each run of its steps folded into one
 * line where it ran, then what those steps show the user (their cards, the turn's subagents in
 * one card, test runs as cards while the steps are folded: opened, the steps show them), and its
 * words between the runs. Once the turn is over its commentary folds too: one line, then its
 * answer.
 */
function TurnView({
  message,
  items,
  isStreaming,
  renderItem,
  onOpenSubagent,
  onOpenTerminal,
  onReviewFile,
}: {
  message: CrowdexUIMessage;
  items: readonly MessagePartRenderItem[];
  isStreaming: boolean;
  renderItem: (item: MessagePartRenderItem) => ReactNode;
  onOpenSubagent?: ((toolCallId: string) => void) | undefined;
  onOpenTerminal?: ((terminalId: string) => void) | undefined;
  onReviewFile?: ((message: CrowdexUIMessage, path: string) => void) | undefined;
}) {
  const options = { interruptedError: INTERRUPTED_ERROR, streaming: isStreaming };
  const segments = turnSegments(items, options);
  const subagents = items.flatMap((item) =>
    item.kind === 'standalone' && item.part.type === 'data-subagent' ? [item.part.data] : [],
  );
  let subagentsShown = false;
  const renderShown = (item: MessagePartRenderItem): ReactNode => {
    if (item.kind === 'standalone' && item.part.type === 'data-subagent') {
      if (subagentsShown) return null;
      subagentsShown = true;
      return <SubagentsCard key={item.key} onOpen={onOpenSubagent} subagents={subagents} />;
    }
    return renderItem(item);
  };
  // A file the turn changed opens in Review once its checkpoint is taken (the turn is over).
  const files = messageChangedFiles(message);
  const reviewFile =
    onReviewFile === undefined || files.length === 0
      ? undefined
      : (path: string) => {
          const known = files.find(
            (file) => path === file.path || path.endsWith(`/${file.path}`),
          )?.path;
          if (known !== undefined) onReviewFile(message, known);
        };
  const waiting = turnWaitsOnUser(items);
  const steps = segments.some((segment) => segment.kind === 'steps');
  return (
    <>
      {/* Before its first step, the turn's line says only that it works. */}
      {!steps && (
        <TurnSteps
          layout={turnLayout(items, options)}
          message={message}
          onOpenChange={() => undefined}
          open={false}
          running={isStreaming}
          waiting={waiting}
        />
      )}
      {segments.map((segment) =>
        segment.kind === 'item' ? (
          renderShown(segment.item)
        ) : (
          <TurnStepsRun
            key={segment.key}
            last={segment.last}
            layout={segment.layout}
            message={message}
            onOpenTerminal={onOpenTerminal}
            onReviewFile={reviewFile}
            renderShown={renderShown}
            running={segment.last && isStreaming}
            waiting={segment.last ? waiting : turnWaitsOnUser(segment.layout.steps)}
          />
        ),
      )}
    </>
  );
}

/** One run of a turn's steps: its line, the cards its steps made, its test runs while folded. */
function TurnStepsRun({
  layout,
  last,
  message,
  running,
  waiting,
  renderShown,
  onOpenTerminal,
  onReviewFile,
}: {
  layout: TurnLayout;
  last: boolean;
  message: CrowdexUIMessage;
  running: boolean;
  waiting: boolean;
  renderShown: (item: MessagePartRenderItem) => ReactNode;
  onOpenTerminal?: ((terminalId: string) => void) | undefined;
  onReviewFile?: ((path: string) => void) | undefined;
}) {
  const [open, setOpen] = useState(layout.attention);
  useEffect(() => {
    if (layout.attention) setOpen(true);
  }, [layout.attention]);
  return (
    <>
      <TurnSteps
        final={last}
        layout={layout}
        message={message}
        onOpenChange={setOpen}
        onOpenTerminal={onOpenTerminal}
        open={open}
        running={running}
        waiting={waiting}
        {...(onReviewFile === undefined ? {} : { onReviewFile })}
      />
      {layout.shown.map(renderShown)}
      {!open &&
        layout.testRuns.map((run) => (
          <CommandCard
            command={run.tool === null ? 'Command' : toolDetail(run.tool, null)}
            key={`${message.id}-test-${run.terminal.data.toolCallId}`}
            onOpen={
              onOpenTerminal === undefined
                ? undefined
                : () => onOpenTerminal(run.terminal.data.terminalId)
            }
            summary={run.summary}
            terminal={run.terminal.data}
          />
        ))}
    </>
  );
}

/**
 * Where each card shows: after the message that was last when it was put, or after the reply to
 * it when that message asked the agent (a card put during a turn follows the turn). Cards whose
 * message is not shown come last.
 */
export function cardsByMessage(
  messages: readonly CrowdexUIMessage[],
  cards: readonly ConversationCard[],
): { after: Map<string | null, ConversationCard[]>; rest: ConversationCard[] } {
  const after = new Map<string | null, ConversationCard[]>();
  const rest: ConversationCard[] = [];
  const index = new Map(messages.map((message, position) => [message.id, position]));
  for (const card of cards) {
    let anchor: string | null = card.afterMessageId;
    const position = anchor === null ? undefined : index.get(anchor);
    if (anchor !== null && position === undefined) {
      rest.push(card);
      continue;
    }
    const next = position === undefined ? undefined : messages[position + 1];
    if (position !== undefined && messages[position]?.role === 'user' && next?.role === 'assistant')
      anchor = next.id;
    after.set(anchor, [...(after.get(anchor) ?? []), card]);
  }
  return { after, rest };
}

export function DesktopMessageList({
  embedded = false,
  editingMessageId,
  messages,
  streamingMessageId,
  onEditContainerChange,
  onEditMessage,
  onOpenPlan,
  onOpenSubagent,
  onOpenTerminal,
  onAnnotateMessage,
  onAskMessageSelectionInSideChat,
  onMoreDetailsMessageSelection,
  onReviewMessageChanges,
  onLoadMessageDiff,
  onUndoMessageChanges,
  artifacts,
  onOpenArtifact,
  cards,
  conversation,
  withActions = false,
}: {
  /** The parent already provides the message card. */
  embedded?: boolean;
  editingMessageId?: string | null;
  messages: readonly CrowdexUIMessage[];
  streamingMessageId?: string;
  onEditContainerChange?: (container: HTMLDivElement | null) => void;
  onEditMessage?: (message: CrowdexUIMessage, text: string) => void;
  onOpenPlan?: () => void;
  onOpenSubagent?: (toolCallId: string) => void;
  onOpenTerminal?: (terminalId: string) => void;
  onAnnotateMessage?: (
    message: CrowdexUIMessage,
    text: string,
    anchor: { x: number; y: number },
  ) => void;
  onAskMessageSelectionInSideChat?: (message: CrowdexUIMessage, text: string) => void;
  onMoreDetailsMessageSelection?: (message: CrowdexUIMessage, text: string) => void;
  onReviewMessageChanges?: (diff: string) => void;
  onLoadMessageDiff?: (message: CrowdexUIMessage, path?: string) => Promise<string>;
  onUndoMessageChanges?: MessageChangeControls['undoMessageChanges'];
  /** The conversation's artifacts: a message shows those it created or changed. */
  artifacts?: readonly ArtifactListingEntry[];
  onOpenArtifact?: (artifact: ArtifactListingEntry) => void;
  /** The cards apps put in the conversation, shown after the turns they follow. */
  cards?: readonly ConversationCard[];
  conversation?: string | null;
  withActions?: boolean;
}) {
  const navigation = useRef({ onOpenSubagent, onOpenPlan, onOpenTerminal });
  useLayoutEffect(() => {
    navigation.current = { onOpenSubagent, onOpenPlan, onOpenTerminal };
  });
  const openSubagent = useCallback((id: string) => navigation.current.onOpenSubagent?.(id), []);
  const openPlan = useCallback(() => navigation.current.onOpenPlan?.(), []);
  const openTerminal = useCallback((id: string) => navigation.current.onOpenTerminal?.(id), []);
  const review = useRef({ onLoadMessageDiff, onReviewMessageChanges });
  review.current = { onLoadMessageDiff, onReviewMessageChanges };
  const reviewFile = useCallback((message: CrowdexUIMessage, path: string) => {
    const { onLoadMessageDiff: load, onReviewMessageChanges: show } = review.current;
    if (load === undefined || show === undefined) return;
    void load(message, path).then(show, () => undefined);
  }, []);
  const canReview = onLoadMessageDiff !== undefined && onReviewMessageChanges !== undefined;
  const [selectionTarget, setSelectionTarget] = useState<{
    bounds: { bottom: number; left: number; top: number };
    message: CrowdexUIMessage;
    text: string;
  } | null>(null);
  useEffect(() => {
    if (selectionTarget === null) return;
    const close = (event: Event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-selection-toolbar]') !== null) return;
      setSelectionTarget(null);
    };
    document.addEventListener('pointerdown', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [selectionTarget]);
  // A finger's selection (its handles, no mouseup) in a message offers its actions too.
  const shownRef = useRef(messages);
  shownRef.current = messages;
  useTouchSelection((selection, range) => {
    const id = rangeElement(range)
      ?.closest('[data-selection-message]')
      ?.getAttribute('data-selection-message');
    const message = shownRef.current.find((candidate) => candidate.id === id);
    if (message !== undefined) setSelectionTarget({ ...selection, message });
  }, onAnnotateMessage !== undefined);
  const Content = embedded ? 'div' : MessageContent;
  const child = useChildConversation(conversation ?? null);
  // A group member that passed said nothing (its answer is the group's protocol): no message.
  const shown = messages.filter((message) => !isGroupPassReply(message));
  // The last reply keeps its actions in sight (lot 5a), once it has finished.
  const lastReplyId =
    withActions && streamingMessageId === undefined
      ? shown.findLast((candidate) => candidate.role === 'assistant')?.id
      : undefined;
  const placed = cards !== undefined && conversation ? cardsByMessage(shown, cards) : null;
  const cardsAfter = (anchor: string | null) =>
    placed && conversation
      ? (placed.after.get(anchor) ?? []).map((card) => (
          <ConversationAppCard
            artifacts={artifacts}
            card={card}
            conversation={conversation}
            key={card.id}
            onOpenArtifact={onOpenArtifact}
          />
        ))
      : null;
  const firstUserMessage = messages.find((message) => message.role === 'user')?.id;
  const workspaceImages = useConversationWorkspaceImages(conversation ?? null);
  return (
    <WorkspaceImagesProvider value={workspaceImages}>
      {cardsAfter(null)}
      {steersAsMessages(shown).map((message) => {
        const isStreaming = message.id === streamingMessageId;
        // A group's turn told the member's agent a prompt (protocol): a marker stands for it.
        const groupTurn = groupTurnMetadata(message);
        if (groupTurn?.kind === 'prompt')
          return (
            <Fragment key={message.id}>
              <GroupTurnMarker turn={groupTurn} />
              {cardsAfter(message.id)}
            </Fragment>
          );
        // A child conversation opens on the task it was given, from its parent (design D49).
        if (child !== null && message.id === firstUserMessage)
          return (
            <Fragment key={message.id}>
              <OriginCard child={child}>
                <MessageParts isStreaming={false} message={message} />
              </OriginCard>
              {cardsAfter(message.id)}
            </Fragment>
          );
        if (personaOfMessage(message) !== null)
          return (
            <Fragment key={message.id}>
              <PersonaReply message={message}>
                <MessageParts isStreaming={isStreaming} message={message} />
                {/* A persona's turn is a turn: what it changed and made shows as any turn's. */}
                {shouldShowMessageChanges(message, isStreaming) && (
                  <MessageChangesCard
                    loadDiff={onLoadMessageDiff}
                    message={message}
                    onReview={onReviewMessageChanges}
                    onUndo={onUndoMessageChanges}
                  />
                )}
                {artifacts !== undefined && onOpenArtifact !== undefined && (
                  <MessagePreviews
                    artifacts={artifacts}
                    message={message}
                    onOpenArtifact={onOpenArtifact}
                  />
                )}
              </PersonaReply>
              {cardsAfter(message.id)}
            </Fragment>
          );
        const copyText = withActions ? messageText(message) : '';
        // A steer belongs to the turn it redirected: it is resent by editing the message before.
        const editable =
          message.role === 'user' &&
          !isSteerMessage(message) &&
          copyText.length > 0 &&
          onEditMessage !== undefined;
        const editing = message.id === editingMessageId;
        return (
          <Fragment key={message.id}>
            <Message
              className={
                withActions
                  ? // Its actions hang under it: on a touch screen their 44 px row needs that room,
                    // else the next message covers the lower half of each target.
                    `desktop-message relative ${copyText.length > 0 ? `mb-2 ${MESSAGE_ACTIONS_ROOM}` : ''} ${
                      message.role === 'user' ? 'desktop-user-message' : ''
                    } ${!editing && editable ? 'cursor-pointer' : ''}`
                  : embedded
                    ? 'max-w-none'
                    : undefined
              }
              from={message.role}
              data-selection-message={message.id}
              onMouseUp={(event) => {
                if (onAnnotateMessage === undefined) return;
                const found = selectionWithin(event.currentTarget);
                if (found !== null) setSelectionTarget({ ...found.selection, message });
              }}
              onClick={
                !editing && editable
                  ? () => {
                      if (window.getSelection()?.isCollapsed !== false)
                        onEditMessage(message, copyText);
                    }
                  : undefined
              }
            >
              {editing ? (
                <div className="w-full" ref={onEditContainerChange} />
              ) : (
                <Content
                  className={
                    embedded
                      ? 'flex min-w-0 w-full flex-col gap-2 text-sm'
                      : message.role === 'user'
                        ? 'desktop-user-message-content'
                        : undefined
                  }
                >
                  <MessageParts
                    message={message}
                    isStreaming={isStreaming}
                    onOpenSubagent={onOpenSubagent === undefined ? undefined : openSubagent}
                    onOpenPlan={onOpenPlan === undefined ? undefined : openPlan}
                    onOpenTerminal={onOpenTerminal === undefined ? undefined : openTerminal}
                    onReviewFile={canReview ? reviewFile : undefined}
                  />
                  {message.metadata?.voice === true && (
                    <p className="flex items-center gap-1 text-muted-foreground text-xs">
                      <AudioLines aria-hidden="true" className="size-3" />
                      Said in a call
                    </p>
                  )}
                  {shouldShowMessageChanges(message, isStreaming) && (
                    <MessageChangesCard
                      loadDiff={onLoadMessageDiff}
                      message={message}
                      onReview={onReviewMessageChanges}
                      onUndo={onUndoMessageChanges}
                    />
                  )}
                  {/* As soon as the turn has written it, even while it still runs (D15). */}
                  {artifacts !== undefined && onOpenArtifact !== undefined && (
                    <MessagePreviews
                      artifacts={artifacts}
                      message={message}
                      onOpenArtifact={onOpenArtifact}
                    />
                  )}
                </Content>
              )}
              {!editing && copyText.length > 0 && message.id === lastReplyId ? (
                // Lot 5a: the last reply keeps its actions in sight (copy, read aloud, side chat, rewind).
                <LastReplyActions
                  className="absolute top-full left-0 z-10 mt-1 justify-start"
                  messages={messages}
                  onEditMessage={onEditMessage}
                  onSideChat={onAskMessageSelectionInSideChat}
                  reply={message}
                />
              ) : (
                !editing &&
                copyText.length > 0 && (
                  <MessageActions
                    className={`absolute top-full z-10 mt-1 can-hover:opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 ${
                      message.role === 'user' ? 'right-0 justify-end' : 'left-0 justify-start'
                    }`}
                  >
                    <MessageCopyAction text={copyText} />
                    {editable && (
                      <MessageAction
                        aria-label="Edit message"
                        className={MESSAGE_ACTION}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditMessage(message, copyText);
                        }}
                        tooltip="Edit message"
                      >
                        <Pencil className="size-3.5" />
                      </MessageAction>
                    )}
                  </MessageActions>
                )
              )}
            </Message>
            {cardsAfter(message.id)}
          </Fragment>
        );
      })}
      {placed && conversation
        ? placed.rest.map((card) => (
            <ConversationAppCard
              artifacts={artifacts}
              card={card}
              conversation={conversation}
              key={card.id}
              onOpenArtifact={onOpenArtifact}
            />
          ))
        : null}
      {/* Where a stopped turn says so, after the last message (design D53). */}
      {withActions && <div className="contents" data-turn-stopped-slot />}
      {/* Offline, what was sent waits here, after the turn (D53). */}
      {withActions && <div className="contents" data-queued-slot />}
      {selectionTarget !== null &&
        createPortal(
          <div
            aria-label="Selection actions"
            className={SELECTION_TOOLBAR_CLASS}
            data-selection-toolbar
            onMouseDown={(event) => event.preventDefault()}
            style={selectionToolbarStyle(selectionTarget.bounds, 430)}
            role="toolbar"
          >
            <button
              className={SELECTION_TOOLBAR_DIVIDER_BUTTON_CLASS}
              onClick={() => {
                onAnnotateMessage?.(selectionTarget.message, selectionTarget.text, {
                  x: selectionTarget.bounds.left,
                  y: selectionTarget.bounds.bottom + 8,
                });
                setSelectionTarget(null);
              }}
              type="button"
            >
              Add to chat
            </button>
            <button
              className={SELECTION_TOOLBAR_DIVIDER_BUTTON_CLASS}
              onClick={() => {
                onMoreDetailsMessageSelection?.(selectionTarget.message, selectionTarget.text);
                setSelectionTarget(null);
                window.getSelection()?.removeAllRanges();
              }}
              type="button"
            >
              More details
            </button>
            <button
              className={SELECTION_TOOLBAR_BUTTON_CLASS}
              onClick={() => {
                onAskMessageSelectionInSideChat?.(selectionTarget.message, selectionTarget.text);
                setSelectionTarget(null);
                window.getSelection()?.removeAllRanges();
              }}
              type="button"
            >
              Ask in side chat
            </button>
          </div>,
          document.body,
        )}
    </WorkspaceImagesProvider>
  );
}

const noSubscription = () => () => undefined;

/**
 * Where a conversation's replies read the workspace images they cite: a preview board's files on
 * a preview page, else its file space (the workspace's absolute path lets a reply cite one inside
 * it).
 */
function useConversationWorkspaceImages(conversation: string | null) {
  const shell = useOptionalDesktopShellStoreApi();
  const root = useSyncExternalStore(
    shell?.subscribe ?? noSubscription,
    () => shell?.getState().workspace ?? null,
    () => null,
  );
  return useMemo(
    () =>
      previewWorkspaceImages() ??
      (conversation === null ? null : conversationWorkspaceImages(conversation, root)),
    [conversation, root],
  );
}
